/* eslint-disable react/react-in-jsx-scope */
// cspell:ignore unnormalized

import classNames from 'classnames'
import { MAX_YEAR, MIN_YEAR, YearPicker } from 'components/YearPicker'
import { isEmpty } from 'lodash'
import { FormContainer } from 'scenes/Dashboard/components-next/Forms'
import { Select, SwitchField, TextField } from 'scenes/Dashboard/components-next/Forms/Controllers'
import { makeStyles } from 'tss-react/mui'
import { FastField, Field, FormHelpers, FormManager, getIn } from 'utils-next/forms'
import { ExtendableStyles } from 'utils-next/types'

import { Button, MenuItem, Theme } from '@mui/material'

import { SearchEngineQueryData } from '../../SearchEngine.types'
import { getEmptySearchQueryData, getSearchEngineQueryDataValidation } from './SearchEngineBar.data'
import { calculateDateFieldValue } from './SearchEngineBar.utils'

export type SearchEngineBarProps = ExtendableStyles & {
  initialValues?: SearchEngineQueryData
  handleSubmit: (values: SubmitData, actions: FormHelpers<SubmitData>) => void
  disabled?: boolean
}

export enum SEVersionOptions {
  POOL_ATLAS = 'atlas',
}

export enum SourceOptions {
  ALL = 'all',
  CRUNCHBASE = 'crunchbase',
  XVERUM = 'xverum',
  APOLLO = 'apollo',
  PITCHBOOK = 'pitchbook',
}

export type SubmitData = SearchEngineQueryData & { submitButton?: 'search' | 'download-csv' }

export function SearchEngineBar({ className, initialValues, handleSubmit, disabled }: SearchEngineBarProps) {
  const { classes } = useStyles()

  return (
    <FormManager<SubmitData>
      initialValues={initialValues || getEmptySearchQueryData()}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={getSearchEngineQueryDataValidation()}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formProps) => {
        const { values, isSubmitting, setFieldValue, errors } = formProps
        const { minimumYear, maximumYear } = values

        return (
          <FormContainer
            disabled={disabled || isSubmitting}
            onSubmit={formProps.handleSubmit}
            className={classNames(className, classes.root)}
          >
            <div className={classes.doubleFieldWrapper}>
              <FastField
                component={TextField}
                label="Positive keywords*"
                placeholder="E.g. Emotion crypto"
                name="keywords"
                disabled={isSubmitting}
                fullWidth
                autoFocus={!disabled}
              />
              <FastField
                component={TextField}
                label="Secondary positive keywords"
                name="secondaryKeywords"
                disabled={isSubmitting}
                fullWidth
              />
            </div>
            <div className={classNames(classes.doubleFieldWrapper, classes.spacingBottom)}>
              <FastField
                component={TextField}
                label="Negative keywords"
                placeholder="E.g. Pharma car"
                name="negativeKeywords"
                disabled={isSubmitting}
                fullWidth
              />
              <FastField
                component={TextField}
                label="Secondary negative keywords"
                name="secondaryNegativeKeywords"
                disabled={isSubmitting}
                fullWidth
              />
            </div>

            <div className={classNames(classes.doubleFieldWrapper, classes.spacingBottom)}>
              <FastField
                component={TextField}
                label="Locations"
                placeholder="E.g. New York, ESP"
                name="locations"
                disabled={isSubmitting}
                className={classes.field}
                fullWidth
              />

              <FastField
                component={TextField}
                label="Negative locations"
                placeholder="E.g. New York, ESP"
                name="negativeLocations"
                disabled={isSubmitting}
                className={classes.field}
                fullWidth
              />
            </div>

            <FastField
              component={TextField}
              label="Last funding event"
              placeholder="E.g. seed, pre_seed"
              name="stages"
              disabled={isSubmitting}
              className={classNames(classes.field)}
              fullWidth
            />

            <div className={classNames(classes.field, classes.booleanFields, classes.spacingBottom)}>
              <FastField
                type="checkbox"
                component={SwitchField}
                label="Companies without maturity information included"
                name="includeCompaniesWithoutMaturityInformation"
                disabled={isSubmitting}
                className={classes.inlineField}
                fullWidth
              />
            </div>

            <div className={classNames(classes.doubleFieldWrapper, classes.spacingBottom)}>
              <Field
                component={YearPicker}
                label="Minimum founding year"
                helperText={errors.minimumYear ?? undefined}
                error={getIn(errors, 'minimumYear') ?? false}
                name="minimumYear"
                minDate={new Date(Date.UTC(MIN_YEAR, 1))}
                maxDate={maximumYear ? new Date(Date.UTC(maximumYear, 1)) : new Date(Date.UTC(MAX_YEAR, 1))}
                value={minimumYear ? new Date(Date.UTC(minimumYear, 1)) : null}
                title="Select Jan 1st of a year to include all the companies of that year. Companies without founding date in the Pool will be included in the results"
                className={classes.field}
                disabled={isSubmitting}
                fullWidth
                onChange={(dateValue: Date | null) => {
                  const newMinimumYear = calculateDateFieldValue(dateValue)
                  setFieldValue('minimumYear', newMinimumYear, true)
                }}
              />
              <Field
                component={YearPicker}
                label="Maximum founding year"
                name="maximumYear"
                minDate={minimumYear ? new Date(Date.UTC(minimumYear, 1)) : new Date(Date.UTC(MIN_YEAR, 1))}
                maxDate={new Date(Date.UTC(MAX_YEAR, 1))}
                value={maximumYear ? new Date(Date.UTC(maximumYear, 1)) : null}
                helperText={errors.maximumYear ?? undefined}
                error={getIn(errors, 'maximumYear') ?? false}
                title="Select Dec 31st of a year to include all the companies of that year. Companies without founding date in the Pool will be included in the results"
                className={classes.field}
                disabled={isSubmitting}
                fullWidth
                onChange={(dateValue: Date | null) => {
                  const newMaximumYear = calculateDateFieldValue(dateValue)
                  setFieldValue('maximumYear', newMaximumYear, true)
                }}
              />
            </div>
            <div className={classes.doubleFieldWrapper}>
              <div className={classes.seVersionContainer}>
                <div>Select the Search Engine version to use</div>
                <FastField
                  component={Select}
                  // label="SE Version"
                  variant="standard"
                  name="seVersion"
                  defaultValue={SEVersionOptions.POOL_ATLAS}
                  disabled={isSubmitting}
                  className={classes.select}
                >
                  <MenuItem value={SEVersionOptions.POOL_ATLAS}>ATLAS</MenuItem>
                </FastField>
              </div>
              <div className={classes.sourceFilterContainer}>
                <div>Select what kind of source you want to show</div>
                <FastField
                  component={Select}
                  // label="Extraction source"
                  name="sourceFilter"
                  variant="standard"
                  defaultValue={SourceOptions.ALL}
                  disabled={isSubmitting}
                  className={classes.select}
                >
                  <MenuItem value={SourceOptions.ALL}>All sources</MenuItem>
                  <MenuItem value={SourceOptions.CRUNCHBASE}>CrunchBase</MenuItem>
                  <MenuItem value={SourceOptions.XVERUM}>Xverum</MenuItem>
                  <MenuItem value={SourceOptions.APOLLO}>Apollo</MenuItem>
                  <MenuItem value={SourceOptions.PITCHBOOK}>Pitchbook</MenuItem>
                </FastField>
              </div>
            </div>

            <div className={classes.buttonActions}>
              <Button
                variant="contained"
                onClick={() => {
                  formProps.setFieldValue('submitButton', 'search')
                }}
                color="secondary"
                className={classes.button}
                type="submit"
                disabled={!isEmpty(errors)}
                aria-label="submit"
              >
                Search
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  formProps.setFieldValue('submitButton', 'download-csv')
                }}
                color="primary"
                className={classes.button}
                type="submit"
                aria-label="submit"
              >
                Download CSV
              </Button>
            </div>
          </FormContainer>
        )
      }}
    </FormManager>
  )
}

const useStyles = makeStyles()(({ spacing }: Theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  doubleFieldWrapper: {
    display: 'grid',
    gridGap: spacing(),
    gridTemplateColumns: '1fr 1fr',
    width: '100%',
  },
  field: {
    minWidth: spacing(13),
  },
  select: {
    marginTop: spacing(1),
  },
  dateField: {
    marginTop: spacing(2),
    minWidth: spacing(13),
  },
  seVersionContainer: {
    width: '100%',
    marginTop: spacing(4),
  },
  sourceFilterContainer: {
    width: '100%',
    marginTop: spacing(4),
  },
  buttonActions: {
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  button: {
    marginTop: spacing(2),
    marginLeft: spacing(2),
  },
  inlineField: {
    marginRight: spacing(3),
    flex: '0 0 auto',
  },
  booleanFields: {
    marginTop: spacing(),
    display: 'flex',
    width: '100%',
  },
  spacingBottom: {
    marginBottom: spacing(4),
  },
}))
